import React, { useState } from 'react';
import { Plus, DollarSign, Calendar, FileText } from 'lucide-react';
import { addMonthlyExpense } from '../services/api';

export default function RepExpensesTab({ repId, expenses, onExpenseAdded }) {
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [travel, setTravel] = useState('');
  const [other, setOther] = useState('');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddExpense = async () => {
    const payload = {
      month,
      travelAllowancePaid: travel ? parseFloat(travel) : 0,
      otherExpensesPaid: other ? parseFloat(other) : 0,
      notes: notes.trim() || null,
    };
    if (Number.isNaN(payload.travelAllowancePaid) || payload.travelAllowancePaid < 0) {
      alert('Invalid travel allowance amount');
      return;
    }
    if (Number.isNaN(payload.otherExpensesPaid) || payload.otherExpensesPaid < 0) {
      alert('Invalid other expenses amount');
      return;
    }
    setAdding(true);
    try {
      const result = await addMonthlyExpense(repId, payload);
      if (result.success) {
        onExpenseAdded(result.data.expense);
        setMonth(new Date().toISOString().slice(0, 7));
        setTravel('');
        setOther('');
        setNotes('');
        setShowAddExpense(false);
      } else {
        alert(result.error || 'Failed to add expense');
      }
    } catch (err) {
      // Expense add error handled silently
      alert('Failed to add expense');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign size={20} className="text-primary-900" />
          <h3 className="text-lg font-semibold text-gray-900">Monthly Expenses</h3>
        </div>
        <button
          onClick={() => setShowAddExpense(true)}
          className="bg-primary-900 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-800 transition flex items-center gap-1"
        >
          <Plus size={16} /> Add Expense
        </button>
      </div>

      {/* Summary Card */}
      {expenses.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Total Paid (All Months)</span>
            <span className="text-xl font-bold text-primary-900">
              ₹{expenses.reduce((sum, e) => sum + Number(e.travel_allowance_paid) + Number(e.other_expenses_paid), 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Month (YYYY-MM)</label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Travel Allowance Paid</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={travel}
                onChange={(e) => setTravel(e.target.value)}
                placeholder="0.00"
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Other Expenses Paid</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="0.00"
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowAddExpense(false);
                setMonth(new Date().toISOString().slice(0, 7));
                setTravel('');
                setOther('');
                setNotes('');
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleAddExpense}
              disabled={adding}
              className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-1"
            >
              {adding ? 'Adding...' : 'Add Expense'}
            </button>
          </div>
        </div>
      )}

      {/* Expenses List */}
      {expenses.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <DollarSign size={48} className="mx-auto mb-3" />
          <p className="text-sm">No expenses recorded</p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Month</th>
                <th className="text-right px-4 py-2 font-medium text-gray-700">Travel</th>
                <th className="text-right px-4 py-2 font-medium text-gray-700">Other</th>
                <th className="text-right px-4 py-2 font-medium text-gray-700">Total</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Notes</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Paid At</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">{expense.month}</td>
                  <td className="text-right px-4 py-2">₹{Number(expense.travel_allowance_paid).toFixed(2)}</td>
                  <td className="text-right px-4 py-2">₹{Number(expense.other_expenses_paid).toFixed(2)}</td>
                  <td className="text-right px-4 py-2 font-medium">
                    ₹{(Number(expense.travel_allowance_paid) + Number(expense.other_expenses_paid)).toFixed(2)}
                  </td>
                  <td className="px-4 py-2">
                    {expense.notes ? (
                      <span className="flex items-center gap-1">
                        <FileText size={12} className="text-gray-400" />
                        {expense.notes}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {new Date(expense.paid_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
